import {
  type BaseEvent,
  type ConsentModeEvent,
  type ConsentState,
  type DataCollectionConfig,
  type Destination,
  type GoogleConsentSettings,
  type GTMCentralEvent,
  type GTMEvent,
  type IDataLayerManager
} from '../typings'
import { GA4_ECOMMERCE_EVENTS } from '../constants'
import {
  CENTRAL_DATA_LAYER_NAME,
  dataLayerNames,
  destinationDataLayerName,
  toGoogleConsent
} from '../config'
import { type ConsentManager } from './consent-manager'

export class DataLayerManager implements IDataLayerManager {
  private config: DataCollectionConfig
  private readonly consentManager: ConsentManager
  private dataLayer: GTMCentralEvent[] = []
  private allDataLayersNames: string[]

  static centralGtmDataLayerName = CENTRAL_DATA_LAYER_NAME

  static generateDestinationDataLayerName(destination: Destination): string {
    return destinationDataLayerName(destination)
  }

  constructor(config: DataCollectionConfig, consentManager: ConsentManager) {
    this.config = config
    this.consentManager = consentManager
    this.allDataLayersNames = dataLayerNames(this.config.destinations)

    this.initialize()
  }

  private initialize(): void {
    // Order matters. Anything already queued is adopted first, then the Consent
    // Mode default goes in, and only then is the container injected — so the
    // default is in the data layer before gtm.js can read it. The container
    // script is `async` and could not run earlier anyway, but relying on that
    // would make the ordering accidental rather than stated.
    this.processDataLayerQueue()

    // `HTML` puts the default and the container in one server-rendered block, so
    // repeating the default here would push a second one AFTER gtm.js has run —
    // and possibly after the CMP's update, which can downgrade it.
    if (this.config.manageConsentDefaults && this.config.loadStrategy !== 'HTML') {
      this.setDefaultGoogleConsent({
        ...this.config.consentSettings.defaultConsentState,
        client_timestamp: Date.now()
      })
    }

    this.injectGTMScript()

    this.consentManager.onConsentUpdate(this.handleConsentUpdate.bind(this))

    if (this.config.isDebugMode) {
      console.log('Initialized DataLayerManager')
    }
  }

  private injectGTMScript(): void {
    // Rendered into the page by `GtmBootstrap` — nothing to do here.
    if (this.config.loadStrategy === 'HTML') {
      return
    }

    if (this.config.loadStrategy === 'AFTER_INTERACTIVE' || document.readyState === 'complete') {
      this.injectScripts()
      return
    }

    window.addEventListener('load', () => this.injectScripts())
  }

  /** Google tag gateway serves `gtm.js` from the merchant's own origin. */
  private gtmScriptSrc(containerId: string, dataLayerName: string): string {
    return `${this.config.scriptOrigin}/gtm.js?id=${containerId}&l=${dataLayerName}`
  }

  private injectDestinationGtmScript(destination: Destination): void {
    if (typeof document === 'undefined' || !document.head) {
      return
    }

    // Add explicit checks for existing scripts
    const gtmScriptId = `gtm-script-${destination.id}`
    const gtmInlineId = `gtm-inline-${destination.id}`

    if (document.getElementById(gtmScriptId) || document.getElementById(gtmInlineId)) {
      if (this.config.isDebugMode) {
        console.log(`Destination GTM ${destination.id} scripts already exist`)
      }
      return
    }

    const dataLayerName = DataLayerManager.generateDestinationDataLayerName(destination)

    // Create and configure the GTM script
    const script = document.createElement('script')
    script.id = gtmScriptId
    script.async = true
    script.defer = true
    script.src = this.gtmScriptSrc(destination.id, dataLayerName)

    script.onerror = () => {
      if (this.config.isDebugMode) {
        console.error(`Failed to load destination GTM ${destination.id} script`)
      }
    }

    const inlineScript = document.createElement('script')
    inlineScript.id = gtmInlineId
    inlineScript.textContent = `
    window.${dataLayerName} = window.${dataLayerName} || [];
    window.${dataLayerName}.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js'
    });
  `

    document.head.appendChild(inlineScript)
    document.head.appendChild(script)
  }

  private injectScripts(): void {
    if (!this.config.gtmConfig.centralContainerId) {
      throw new Error('GTM container ID is required')
    }

    if (typeof document === 'undefined' || !document.head) {
      return
    }

    // Add explicit checks for existing scripts
    const gtmScriptId = `gtm-script-${this.config.gtmConfig.centralContainerId}`
    const gtmInlineId = `gtm-inline-${this.config.gtmConfig.centralContainerId}`

    if (document.getElementById(gtmScriptId) || document.getElementById(gtmInlineId)) {
      if (this.config.isDebugMode) {
        console.log('GTM scripts already exist')
      }
      return
    }

    // Create and configure the GTM script
    const script = document.createElement('script')
    script.id = gtmScriptId
    script.async = true
    script.src = this.gtmScriptSrc(
      this.config.gtmConfig.centralContainerId,
      DataLayerManager.centralGtmDataLayerName
    )

    script.onerror = () => {
      if (this.config.isDebugMode) {
        console.error('Failed to load GTM script')
      }
    }

    const inlineScript = document.createElement('script')
    inlineScript.id = gtmInlineId
    inlineScript.textContent = `
      window.${DataLayerManager.centralGtmDataLayerName} = window.${DataLayerManager.centralGtmDataLayerName} || [];
      window.${DataLayerManager.centralGtmDataLayerName}.push({
        'gtm.start': new Date().getTime(),
        event: 'gtm.js'
      });
    `
    // Kept this just in case to test how redirecting events work for ga4 destinations directly
    // inlineScript.textContent = `
    //   window.${DataLayerManager.centralGtmDataLayerName} = window.${DataLayerManager.centralGtmDataLayerName} || [];
    //   function gtag(){window.${DataLayerManager.centralGtmDataLayerName}.push(arguments);}
    //   gtag('js', new Date());
    //   gtag('config', '${this.config.gtmConfig.centralContainerId}');
    // `

    document.head.appendChild(inlineScript)
    document.head.appendChild(script)

    if (document.body) {
      const noscriptId = `gtm-noscript-${this.config.gtmConfig.centralContainerId}`
      if (!document.getElementById(noscriptId)) {
        const noscript = document.createElement('noscript')
        noscript.id = noscriptId
        noscript.innerHTML = `
        <iframe src='${this.config.scriptOrigin}/ns.html?id=${this.config.gtmConfig.centralContainerId}'
          height='0' width='0' style='display:none;visibility:hidden'></iframe>
      `
        document.body.insertBefore(noscript, document.body.firstChild)
      }
    }

    this.config.destinations.forEach((destination) => {
      try {
        switch (destination.type.toLowerCase()) {
          case 'ga4':
            // handleGA4(destination);
            break
          case 'gtm':
            this.injectDestinationGtmScript(destination)
            break
          default:
            if (this.config.isDebugMode) {
              console.warn('Unsupported destination type:', destination.type)
            }
        }
      } catch (destError) {
        if (this.config.isDebugMode) {
          console.error('Error processing destination:', destError)
        }
      }
    })
  }

  private readonly handleConsentUpdate = (state: ConsentState): void => {
    this.updateGoogleConsent(state)
  }

  private transformConsentStateToGoogleConsent(state: ConsentState): GoogleConsentSettings {
    return toGoogleConsent(state)
  }

  private updateGoogleConsent(state: ConsentState): void {
    this.allDataLayersNames.forEach((dataLayerName) => {
      try {
        const settings = this.transformConsentStateToGoogleConsent(state)
        const consentEvent: ConsentModeEvent = {
          event: 'consent_update',
          consent: settings
        }

        if (this.config.isDebugMode) {
          console.log(`Updating Google consent for ${dataLayerName}:`, consentEvent)
        }

        window[dataLayerName] = window[dataLayerName] || []

        const gtag = function gtag(..._args: any[]) {
          // eslint-disable-next-line prefer-rest-params -- need to pass arguments
          window[dataLayerName].push(arguments)
        }
        gtag?.('consent', 'update', settings)
        gtag?.('set', 'ads_data_redaction', settings.ad_storage === 'denied')

        window[dataLayerName]?.push(consentEvent)
      } catch (error) {
        if (this.config.isDebugMode) {
          console.error(`Failed to update Google consent for ${dataLayerName}:`, error)
        }
      }
    })
  }

  private setDefaultGoogleConsent(state: ConsentState): void {
    this.allDataLayersNames.forEach((dataLayerName) => {
      try {
        const settings = this.transformConsentStateToGoogleConsent(state)
        const consentEvent: ConsentModeEvent = {
          event: 'consent_update',
          consent: settings
        }

        if (this.config.isDebugMode) {
          console.log(`Updating Google consent for ${dataLayerName}:`, consentEvent)
        }

        window[dataLayerName] = window[dataLayerName] || []

        const gtag = function gtag(..._args: any[]) {
          // eslint-disable-next-line prefer-rest-params -- need to pass arguments
          window[dataLayerName].push(arguments)
        }

        gtag('consent', 'default', {
          ...settings,
          wait_for_update: 500
        })
        gtag('set', 'ads_data_redaction', settings.ad_storage === 'denied')
        gtag('set', 'url_passthrough', false)

        window[dataLayerName].push(consentEvent)
      } catch (error) {
        if (this.config.isDebugMode) {
          console.error(`Failed to set default Google consent for ${dataLayerName}:`, error)
        }
      }
    })
  }

  public pushEvent<T extends BaseEvent>(name: T['event'], params: Omit<T, 'event'>): void {
    // Check if this is an ecommerce event that requires clearing
    const isEcommerceEvent = this.isEcommerceEvent(name, params)

    if (isEcommerceEvent) {
      this.clearEcommerceParams()
    }

    this.allDataLayersNames.forEach((dataLayerName) => {
      let event: GTMEvent = {
        event: name,
        ...params
      }

      if (dataLayerName === DataLayerManager.centralGtmDataLayerName) {
        event = {
          ...event,
          store_id: this.config.storeId,
          client_timestamp: Date.now(),
          consent_state: this.consentManager.getCurrentConsentState()
        } satisfies GTMCentralEvent

        this.dataLayer.push(event as GTMCentralEvent)
      }

      try {
        if (this.config.isDebugMode) {
          console.log(`Pushing event to ${dataLayerName}:`, event)
        }

        window[dataLayerName] = window[dataLayerName] || []
        window[dataLayerName]?.push(event)
      } catch (error) {
        if (this.config.isDebugMode) {
          console.error('Failed to push event:', error)
        }
      }
    })
  }

  /**
   * Clear ecommerce object in all dataLayers to prevent data pollution
   * This should be called before pushing any ecommerce event
   */
  private clearEcommerceParams(): void {
    this.allDataLayersNames.forEach((dataLayerName) => {
      try {
        window[dataLayerName] = window[dataLayerName] || []
        window[dataLayerName].push({ ecommerce: null })

        if (this.config.isDebugMode) {
          console.log(`Cleared ecommerce data for ${dataLayerName}`)
        }
      } catch (error) {
        if (this.config.isDebugMode) {
          console.error(`Failed to clear ecommerce for ${dataLayerName}:`, error)
        }
      }
    })
  }

  /**
   * Check if an event is an ecommerce event that requires clearing
   */
  private isEcommerceEvent(eventName: string, params: any): boolean {
    // Check if it's a standard GA4 ecommerce event
    if (GA4_ECOMMERCE_EVENTS.includes(eventName as any)) {
      return true
    }

    // Check if the event contains ecommerce data
    return typeof params === 'object' && 'ecommerce' in params
  }

  public destroy(): void {
    this.consentManager.removeConsentUpdateCallback(this.handleConsentUpdate)
    this.dataLayer = []

    if (this.config.isDebugMode) {
      console.log('Destroyed DataLayerManager')
    }
  }

  public getDataLayerHistory(): GTMCentralEvent[] {
    return [...this.dataLayer]
  }

  private processDataLayerQueue(): void {
    const existingDataLayer = window[DataLayerManager.centralGtmDataLayerName] ?? []
    window[DataLayerManager.centralGtmDataLayerName] = existingDataLayer

    existingDataLayer.forEach((event: any) => {
      if (this.isGTMEvent(event)) {
        this.dataLayer.push(event)
      }
    })
  }

  private isGTMEvent(event: GTMCentralEvent | ConsentModeEvent | object): event is GTMCentralEvent {
    return 'store_id' in event && 'consent_state' in event
  }
}
